"use client";

import React, { useEffect, useState, useRef } from "react";
import { Check, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { ClockLoader } from "react-spinners";
import toast from "react-hot-toast";
import { motion, useAnimation, useInView, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getCouponByCode, getPlans } from "../utils/api";
import { initializePayment } from '../utils/paymentUtils';
import { PaymentType } from '../utils/paymentTypes';

export default function StartupRegPricing({ onPlanSelect, onSkipPayment, registrationData, formData, coupon }) {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlanId, setProcessingPlanId] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [tactosCoupon, setTactosCoupon] = useState("");

    const navigate = useNavigate();
    const sectionRef = useRef(null);
    const inView = useInView(sectionRef, { once: true, margin: "-100px" });
    const controls = useAnimation();
    const INITIAL_FEATURES_COUNT = 3;

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
    };


    useEffect(() => {
        if (inView) controls.start("visible");
    }, [inView, controls]);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setLoading(true);
                const all = await getPlans();
                const startupPlans = all
                    .filter(p => p.type === "Membership")
                    .map(p => ({
                        id: p._id,
                        name: p.title,
                        price: p.price,
                        features: p.features?.length ? p.features : ["No features listed"],
                        tenure: p.tenure || '1 month',
                    }));
                setPlans(startupPlans);
            } catch (err) {
                console.error(err);
                setError("Failed to load plans");
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const toggleCardExpansion = planId =>
        setExpandedCards(prev => ({ ...prev, [planId]: !prev[planId] }));

    const handleSelectPlan = async (plan) => {
        try {
            setProcessingPlanId(plan.id);

            // Get registration data from localStorage
            const registrationData = JSON.parse(localStorage.getItem('registrationData'));
            const token = localStorage.getItem('Startuptoken');

            console.log('Registration Data:', token);
            if (!token) {
                throw new Error('Registration data not found. Please try registering again.');
            }


            sessionStorage.setItem(
                `subscription_plan`,
                JSON.stringify(plan)
            );

            // Calculate final amount (with coupon if available)
            let finalAmount = plan.price;

            if (coupon && coupon.discount) {
                finalAmount = finalAmount - (finalAmount * (coupon.discount / 100));
                console.log(`Coupon applied: ${coupon.code}, Discount: ${coupon.discount}%, Final Amount: ${finalAmount}`);
            }

            // Initialize payment
            const paymentResult = await initializePayment({
                type: PaymentType.SUBSCRIPTION,
                amount: finalAmount,
                token,
                returnUrl: `${window.location.origin}/payment-callback`
            });

        } catch (error) {
            console.error('Payment Error:', error);
            toast.error(error.message || 'Payment processing failed');
        } finally {
            setProcessingPlanId(null);
        }
    };

    if (error) return <div className="py-12 text-center text-red-600">{error}</div>;

    const gridCols = plans.length === 1 ? "grid-cols-1" :
        plans.length === 2 ? "grid-cols-1 md:grid-cols-2" :
            "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

    return (
        <div className="py-8 px-4" ref={sectionRef}>
            <motion.div
                className="text-center mb-8"
                initial="hidden"
                animate={controls}
                variants={cardVariants}
            >
                <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
                    Choose Your <span className="text-blue-600">Startup Plan</span>
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Select a plan to complete your startup registration
                </p>
            </motion.div>

            <div className={`grid ${gridCols} gap-6 max-w-5xl mx-auto`}>
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                            <div className="space-y-4">
                                <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
                                <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto"></div>
                                <div className="space-y-3">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="flex items-center space-x-3">
                                            <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                                            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                ) : plans.map((plan) => {
                    const isExpanded = expandedCards[plan.id];
                    const visibleFeatures = plan.features.slice(0, INITIAL_FEATURES_COUNT);
                    const hasMoreFeatures = plan.features.length > INITIAL_FEATURES_COUNT;

                    // Calculate discounted price if coupon exists
                    let displayPrice = plan.price;
                    if (coupon?.discount) {
                        displayPrice = plan.price - (plan.price * (coupon.discount / 100));
                    }

                    return (
                        <motion.div
                            key={plan.id}
                            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                            animate={controls}
                            variants={cardVariants}
                        >
                            <div className="p-6">
                                <div className="text-center mb-4">
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                                    <div className="mb-3">
                                        {coupon?.discount ? (
                                            <div className="flex justify-center items-center gap-2">
                                                <span className="text-2xl font-bold text-blue-600">
                                                    ₹{displayPrice.toFixed(2)}
                                                </span>
                                                <span className="text-gray-400 line-through text-sm">
                                                    ₹{plan.price.toFixed(2)}
                                                </span>
                                                <span className="text-green-600 text-sm font-semibold">
                                                    {coupon.discount}% OFF
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-2xl font-bold text-blue-600">
                                                ₹{plan.price.toFixed(2)}
                                            </span>
                                        )}
                                        <span className="text-gray-500 text-sm ml-1">
                                            / {Math.round(plan.tenure / 30)} {Math.round(plan.tenure / 30) > 1 ? 'Months' : 'Month'}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleSelectPlan(plan)}
                                    disabled={processingPlanId === plan.id}
                                    className={`w-full mb-4 px-4 py-2 rounded-lg font-medium text-sm
                    transition-all duration-300
                    ${processingPlanId === plan.id
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
                                        }`}
                                >
                                    {processingPlanId === plan.id ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <ClockLoader size={16} color="#6b7280" />
                                            <span>Processing...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center gap-2">
                                            <span>Select Plan</span>
                                            <ArrowRight size={16} />
                                        </div>
                                    )}
                                </button>

                                <div className="space-y-3">
                                    <h4 className="font-medium text-gray-900 text-sm mb-2 border-b border-gray-100 pb-1">
                                        Included Features:
                                    </h4>
                                    <ul className="space-y-2">
                                        {visibleFeatures.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-700 text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {hasMoreFeatures && (
                                        <>
                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.ul
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="space-y-2 overflow-hidden"
                                                    >
                                                        {plan.features.slice(INITIAL_FEATURES_COUNT).map((feature, idx) => (
                                                            <li key={idx + INITIAL_FEATURES_COUNT} className="flex items-start gap-2">
                                                                <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                                                <span className="text-gray-700 text-sm">{feature}</span>
                                                            </li>
                                                        ))}
                                                    </motion.ul>
                                                )}
                                            </AnimatePresence>
                                            <button
                                                onClick={() => toggleCardExpansion(plan.id)}
                                                className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                                            >
                                                {isExpanded ? "Show Less" : `Show More (${plan.features.length - INITIAL_FEATURES_COUNT})`}
                                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

        </div>
    );
}